import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LogService } from 'src/app/services/common/log.service';
import { SubscriberService } from 'src/app/services/app/subscriber.service';

@Component({
    standalone: true,
    selector: 'app-subscriber-forgot-password',
    templateUrl: './subscriber-forgot-password.component.html',
    styleUrls: ['./subscriber-forgot-password.component.css'],
    imports: [FormsModule, ReactiveFormsModule]
})
export class SubscriberForgotPasswordComponent implements OnInit {

  firstFormGroup : FormGroup;
  secondFormGroup : FormGroup;
  thirdFormGroup : FormGroup;

  /** 0: email, 1: OTP, 2: new password, 3: done */
  activeStep = 0;

  IsContactValid = false;
  IsSuccessOTP = true;
  IsOTPValid = false;
  IsPasswordNotMatch = false;
  IsContactFormatInvalid = false;

  hide = true;
  confirmhide = true;

  otpDigits: string[] = ['', '', '', '', '', ''];

  constructor(private router:Router,
    private subscriberService:SubscriberService,
    private _formBuilder: FormBuilder,
    private logService:LogService){}

  ngOnInit(): void {
    try{
      this.firstFormGroup = this._formBuilder.group({
        EmailID: ['', Validators.required],
      });
      this.secondFormGroup = this._formBuilder.group({
        EmailID: [''],
        OTP: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
      });
      this.thirdFormGroup = this._formBuilder.group({
        EmailID: [''],
        Password: ['', Validators.required],
        ConfirmPassword: ['', Validators.required]
      });
    }catch(ex){
      this.logService.InsertExceptionLog(ex,"Subscriber Forgot Password","ngOnInit");
      console.log(ex);
    }    
  }

  ResetPassword(){
    try{
      this.IsContactValid = false;
      this.IsContactFormatInvalid = false;
      const contact = String(this.firstFormGroup.value.EmailID ?? '').trim();
      this.firstFormGroup.patchValue({ EmailID: contact });
      this.firstFormGroup.get('EmailID')?.markAsTouched();

      if (!this.firstFormGroup.valid) {
        return;
      }

      if (!this.isValidEmailOrMobile(contact)) {
        this.IsContactFormatInvalid = true;
        return;
      }

      this.subscriberService.SetOtpAndEmailForSubscriber(this.firstFormGroup.value).subscribe(
        (result) => {
          if(result.status > 0 && result.isSuccess == 1){
            this.otpDigits = ['', '', '', '', '', ''];
            this.secondFormGroup.get('OTP')?.reset('');
            this.IsOTPValid = false;
            this.activeStep = 1;
          }
          else{
            this.IsContactValid = true;
          }
        }, (err: Error) => {
          console.log(err.message);
        });
    }catch(ex){
      this.logService.InsertExceptionLog(ex,"Subscriber Forgot Password","ResetPassword");
      console.log(ex);
    }    
  }

  private isValidEmailOrMobile(value: string): boolean {
    const contact = String(value ?? '').trim();
    if (!contact) {
      return false;
    }
    if (/^\d{10}$/.test(contact)) {
      return true;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact);
  }

  clearContactError(): void {
    this.IsContactValid = false;
    this.IsContactFormatInvalid = false;
  }

  ValidateOTP(){
    try{
      this.syncOtpControl();
      this.secondFormGroup.patchValue({ EmailID: this.firstFormGroup.value.EmailID });
      this.secondFormGroup.get('OTP')?.markAsTouched();
      if(this.secondFormGroup.valid){
        this.IsOTPValid = false;
        this.subscriberService.ValidateOTPForSubscriber(this.secondFormGroup.value).subscribe(
          (result) => {
            if(result.status > 0 && result.isSuccess == 1){
              this.activeStep = 2;
            }
            else{
              this.IsOTPValid = true;
            }
          }, (err: Error) => {
            console.log(err.message);
          })
      }
    }catch(ex){
      this.logService.InsertExceptionLog(ex,"Subscriber Forgot Password","ValidateOTP");
      console.log(ex);
    }
  }

  syncOtpControl(): void {
    const otp = this.otpDigits.join('');
    this.secondFormGroup.get('OTP')?.setValue(otp);
    this.secondFormGroup.get('OTP')?.updateValueAndValidity({ emitEvent: false });
  }

  onOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = (input.value || '').replace(/\D/g, '');
    const digit = value.slice(-1);
    this.otpDigits[index] = digit;
    input.value = digit;
    this.IsOTPValid = false;
    this.syncOtpControl();
    if (digit && index < this.otpDigits.length - 1) {
      const next = input.parentElement?.querySelectorAll<HTMLInputElement>('.forgot-otp-box')[index + 1];
      next?.focus();
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace' && !input.value && index > 0) {
      const prev = input.parentElement?.querySelectorAll<HTMLInputElement>('.forgot-otp-box')[index - 1];
      prev?.focus();
      prev?.select();
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) {
      return;
    }
    for (let i = 0; i < this.otpDigits.length; i++) {
      this.otpDigits[i] = pasted[i] || '';
    }
    this.otpDigits = [...this.otpDigits];
    this.IsOTPValid = false;
    this.syncOtpControl();
    const boxes = (event.target as HTMLElement).parentElement?.querySelectorAll<HTMLInputElement>('.forgot-otp-box');
    const focusIndex = Math.min(pasted.length, this.otpDigits.length - 1);
    boxes?.[focusIndex]?.focus();
  }

  ResendOTP(): void {
    try {
      this.IsOTPValid = false;
      this.otpDigits = ['', '', '', '', '', ''];
      this.secondFormGroup.get('OTP')?.reset('');
      if (this.firstFormGroup.valid) {
        this.subscriberService.SetOtpAndEmailForSubscriber(this.firstFormGroup.value).subscribe(
          (result) => {
            if (result.status > 0 && result.isSuccess == 1) {
              this.IsSuccessOTP = true;
            } else {
              this.IsContactValid = true;
              this.activeStep = 0;
            }
          },
          (err: Error) => {
            console.log(err.message);
          }
        );
      }
    } catch (ex) {
      this.logService.InsertExceptionLog(ex, 'Subscriber Forgot Password', 'ResendOTP');
      console.log(ex);
    }
  }

  GoBackToEmail(): void {
    this.activeStep = 0;
    this.IsOTPValid = false;
    this.otpDigits = ['', '', '', '', '', ''];
    this.secondFormGroup.get('OTP')?.reset('');
  }

  ResetSavePassword(){
    try{
      if(this.thirdFormGroup.value.Password != this.thirdFormGroup.value.ConfirmPassword){
        this.IsPasswordNotMatch = true;
      }
      else{
        this.IsPasswordNotMatch = false;
        this.thirdFormGroup.value.EmailID = this.firstFormGroup.value.EmailID;
        if(this.thirdFormGroup.valid){
          this.subscriberService.UpdateSubscriberPassword(this.thirdFormGroup.value).subscribe(
            (result) => {
              if(result.status > 0 && result.isSuccess == 1){
                this.activeStep = 3;
              }
              else{
                console.log('reset password failed');
              }
            }, (err: Error) => {
              console.log(err.message);
            })
        }
      }
    }catch(ex){
      this.logService.InsertExceptionLog(ex,"Subscriber Forgot Password","ResetSavePassword");
      console.log(ex);
    }
  }

  ResetIsSuccessOTP(){
    this.IsSuccessOTP = false;
  }

  RedirectToLogin(){
    this.router.navigate(
      ['subscriber-login']
    ); 
  }  
}
